import {
  CellContext,
  Cell as CellType,
  ColumnDefTemplate,
  Row,
  flexRender,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import ExpandableCell from './ExpandableCell';
import SelectorCell from './SelectorCell';
import { CellLeftPadding } from './common';

type CellContentProps<T> = {
  cell: CellType<T, unknown>;
  row: Row<T>;
};

type ContentProps<T> = {
  cellRenderer: ColumnDefTemplate<CellContext<T, unknown>> | undefined;
  context: CellContext<T, unknown>;
};

const Content = <T,>({ cellRenderer, context }: ContentProps<T>) => (
  <>{flexRender(cellRenderer, context)}</>
);

/**
 * CellContent component is responsible for rendering the correct content
 * based on the provided cell and row properties.
renderCellContent * @returns {ReactNode} - The rendered cell content
 */
const Cell = <T,>({ cell, row }: CellContentProps<T>): JSX.Element => {
  const { depth, groupingValue, groupingColumnId } = row;

  const { getIsAggregated, getIsPlaceholder, getContext, column, getValue } =
    cell;

  const isAggregated = getIsAggregated();
  const context = getContext();
  const cellRenderer = isAggregated
    ? column.columnDef.aggregatedCell ?? column.columnDef.cell
    : column.columnDef.cell;

  const { isSelectColumn, isGroupingColumn } =
    context.column.columnDef.meta || {};

  const cellValue = getValue();
  const canRowExpand = row.getCanExpand();
  const isPlaceholder = getIsPlaceholder();

  const allCells = row.getAllCells();
  const currentCell = useMemo(
    () => allCells.find((c) => c.column.id === groupingColumnId),
    [allCells, groupingColumnId],
  );

  const isExpandablePlaceholder = isPlaceholder && canRowExpand;
  const isGroupedCell = cellValue != null && groupingValue === cellValue;
  const shouldOmit = isExpandablePlaceholder || isGroupedCell;
  const isExpandableCell = Boolean(isGroupingColumn === true && canRowExpand);

  if (isExpandableCell && currentCell)
    return <ExpandableCell<T> cell={currentCell} />;

  if (shouldOmit) return <div />;

  if (Boolean(isSelectColumn) && row.subRows.length > 0)
    return <SelectorCell row={row} />;

  if (isGroupingColumn !== true)
    return <Content cellRenderer={cellRenderer} context={context} />;

  return (
    <CellLeftPadding depth={depth + 1}>
      <Content cellRenderer={cellRenderer} context={context} />
    </CellLeftPadding>
  );
};

export default Cell;
